import React, { useState } from "react";
import styles from "./RefinanceForm.module.css";
import WelcomeScreen from "./WelcomeScreen";

function SummarySubmit({ formData }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      console.log("📦 Sending to Lambda:", formData);

      const response = await fetch(
        "https://duh7zoxbvd.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixSendToMercury",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const result = await response.json();
      const data =
        typeof result.body === "string" ? JSON.parse(result.body) : result;

      console.log("📩 Lambda Response Parsed:", data);

      if (data.success) {
        console.log("✅ Mercury submission successful:", data);
        setSubmitted(true);
        localStorage.removeItem("clientRefinanceData");
      } else {
        console.error("❌ Mercury API failed:", data);
        alert(
          `Mercury API failed: ${data.message || "Unknown error — please try again."}`
        );
      }
    } catch (err) {
      console.error("❌ Submit error:", err);
      alert(
        "Something went wrong submitting your refinance form. Please try again or contact our team."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <WelcomeScreen formData={formData} />;
  }

  // ✅ Combine all liabilities
  const allLiabilities = [
    ...(formData.applicant1?.liabilities || []),
    ...(formData.applicant2?.liabilities || []),
  ];

  // ✅ Build readable summary including repayments
  const liabilitySummary =
    allLiabilities.length > 0
      ? allLiabilities
          .map((l) => {
            const parts = [];
            if (l.limit) parts.push(`limit $${l.limit}`);
            if (l.balance) parts.push(`owing $${l.balance}`);
            if (l.repayment) parts.push(`repayment $${l.repayment}/mo`);
            const details = parts.length ? parts.join(", ") : "no amount listed";
            return `💳 ${l.type} – (${details})`;
          })
          .join("\n")
      : "None listed";

  // ✅ Calculate total repayments
  const totalRepayments = allLiabilities.reduce((sum, l) => {
    const repay = parseFloat(l.repayment?.replace(/,/g, "")) || 0;
    return sum + repay;
  }, 0);

  return (
    <div className={styles.container}>
      <h3 className={styles.heading}>Submit Application</h3>
      <p className={styles.description}>
        Click below to securely send your refinance details to Mortgage Matrix.
      </p>

      <div className={styles.summaryBox}>
        <h4>Quick Summary</h4>

        {/* Applicant 1 */}
        <p>
          <strong>Name:</strong>{" "}
          {`${formData.applicant1?.firstName || ""} ${
            formData.applicant1?.lastName || ""
          }`}
        </p>
        <p>
          <strong>Email:</strong> {formData.applicant1?.email || "N/A"}
        </p>
        <p>
          <strong>Phone:</strong> {formData.applicant1?.phone || "N/A"}
        </p>
        <p>
          <strong>Marital Status:</strong>{" "}
          {formData.applicant1?.maritalStatus || "N/A"}
        </p>
        <p>
          <strong>Dependants:</strong> {formData.applicant1?.dependants || "N/A"}
        </p>

        {/* Property */}
        <p>
          <strong>Property Address:</strong>{" "}
          {formData.propertyAddress?.formatted ||
            formData.propertyAddress ||
            "N/A"}
        </p>
        <p>
          <strong>Property Value:</strong> $
          {formData.propertyValue?.toLocaleString() || "N/A"}
        </p>
        <p>
          <strong>Loan Balance:</strong> $
          {formData.loanBalance?.toLocaleString() || "N/A"}
        </p>
        <p>
          <strong>Refinance Goal:</strong> {formData.refinanceGoal || "N/A"}
        </p>

        {/* Co-Applicant */}
        {formData.refinancingWithSomeone && (
          <>
            <h5>Co-Applicant</h5>
            <p>
              <strong>Name:</strong>{" "}
              {`${formData.applicant2?.firstName || ""} ${
                formData.applicant2?.lastName || ""
              }`}
            </p>
            <p>
              <strong>Email:</strong> {formData.applicant2?.email || "N/A"}
            </p>
            <p>
              <strong>Phone:</strong> {formData.applicant2?.phone || "N/A"}
            </p>
          </>
        )}

        {/* Employment & Income */}
        <h5>Employment & Income</h5>

        <p>
          <strong>Applicant 1 – Employment Type:</strong>{" "}
          {formData.applicant1?.employmentType || "N/A"}
        </p>
        <p>
          <strong>Applicant 1 – Employer:</strong>{" "}
          {formData.applicant1?.employerName || "N/A"}
        </p>
        <p>
          <strong>Applicant 1 – Income:</strong>{" "}
          {formData.applicant1?.income
            ? `$${Number(formData.applicant1.income).toLocaleString()}/yr`
            : "N/A"}
        </p>

        {formData.refinancingWithSomeone && (
          <>
            <p>
              <strong>Applicant 2 – Employment Type:</strong>{" "}
              {formData.applicant2?.employmentType || "N/A"}
            </p>
            <p>
              <strong>Applicant 2 – Employer:</strong>{" "}
              {formData.applicant2?.employerName || "N/A"}
            </p>
            <p>
              <strong>Applicant 2 – Income:</strong>{" "}
              {formData.applicant2?.income
                ? `$${Number(formData.applicant2.income).toLocaleString()}/yr`
                : "N/A"}
            </p>
          </>
        )}

        {/* Liabilities */}
        <h5>Liabilities</h5>
        <pre className={styles.liabilitySummary}>{liabilitySummary}</pre>

        {totalRepayments > 0 && (
          <p className={styles.totalRepayment}>
            <strong>Total Monthly Repayments:</strong>{" "}
            ${totalRepayments.toLocaleString()}
          </p>
        )}
      </div>

      {/* Submit Button (Full Width) */}
      <div className={styles.navBtns}>
        <button
          className={`${styles.nextBtn} ${styles.fullWidthBtn} ${
            submitting ? styles.loadingBtn : ""
          }`}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <div className={styles.spinnerContainer}>
              <div className={styles.spinner}></div>
              <span>Submitting...</span>
            </div>
          ) : (
            "Submit Application"
          )}
        </button>
      </div>
    </div>
  );
}

export default SummarySubmit;
